import React, { useState } from "react";
import { jwtDecode } from "jwt-decode";
import { FaPaste, FaTrash } from "react-icons/fa";
import { Helmet } from "react-helmet";

export default function JWTDecoder() {
  const [jwtToken, setJwtToken] = useState("");
  const [decodedToken, setDecodedToken] = useState(null);
  const [error, setError] = useState("");

  const handleDecode = () => {
    try {
      if (!jwtToken.trim()) {
        setError("Please enter a valid JWT token.");
        setDecodedToken(null);
        return;
      }

      const decoded = jwtDecode(jwtToken);
      setDecodedToken(decoded);
      setError("");
    } catch (err) {
      setError("Invalid JWT token format.");
      setDecodedToken(null);
    }
  };

  const handleClear = () => {
    setJwtToken("");
    setDecodedToken(null);
    setError("");
  };

  return (
    <>
      <Helmet>
        <title>JWT Decoder - Decode JSON Web Tokens Instantly | WebUtility.online</title>
        <meta name="description" content="Free online JWT decoder tool. Paste your JWT token and instantly decode its payload. No login required, secure and fast." />
        <meta name="keywords" content="JWT decoder, decode jwt, json web token, jwt token parser, online jwt tool, WebUtility" />
        <meta property="og:title" content="JWT Decoder - Decode JSON Web Tokens Instantly" />
        <meta property="og:description" content="Use WebUtility's free JWT decoder to quickly view the payload of your JSON Web Token." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://webutility.online/jwt-decoder" />
        <meta property="og:image" content="https://webutility.online/assets/og-jwt-decoder.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="JWT Decoder - Free Online Tool" />
        <meta name="twitter:description" content="Decode JWT tokens online with WebUtility. No signup required." />
        <meta name="twitter:image" content="https://webutility.online/assets/og-jwt-decoder.jpg" />
      </Helmet>

      <div className="container mx-auto p-6 max-w-md-2xl bg-white shadow-lg rounded-xl">
        {/* Heading */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">JWT Decoder</h2>
          <p className="text-gray-600">Decode JSON Web Tokens instantly.</p>
        </div>

        {/* Input */}
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Enter JWT Token:</label>
          <textarea
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="4"
            placeholder="Paste your JWT token here..."
            value={jwtToken}
            onChange={(e) => setJwtToken(e.target.value)}
          ></textarea>
        </div>

        {/* Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={handleDecode}
            className="flex-1 flex items-center justify-center bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            <FaPaste className="mr-2" /> Decode
          </button>
          <button
            onClick={handleClear}
            className="flex-1 flex items-center justify-center bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 transition"
          >
            <FaTrash className="mr-2" /> Clear
          </button>
        </div>

        {/* Error Message */}
        {error && <p className="text-red-500 mt-4">{error}</p>}

        {/* Decoded Output */}
        {decodedToken && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-800">Decoded JWT:</h3>
            <div className="bg-gray-100 p-4 rounded-lg overflow-auto text-sm">
              <pre className="whitespace-pre-wrap break-all text-gray-700">
                {JSON.stringify(decodedToken, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Guide Section */}
      <section className="container mx-auto p-6 max-w-md-2xl bg-white shadow-lg rounded-xl mt-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">🛠 How to Use the JWT Decoder</h2>
        <p className="text-gray-600 mb-4">
          The <strong>JWT Decoder</strong> tool helps you instantly decode JSON Web Tokens (JWTs) without any signups or installations. Simply paste your JWT token, and the tool will decode it for you to view its payload content.
        </p>
        <ul className="list-disc list-inside space-y-2 mb-4">
          <li>Copy your JWT token from your application or API response.</li>
          <li>Paste the JWT token into the input field.</li>
          <li>Click on the <strong>“Decode”</strong> button to view the decoded payload.</li>
          <li>Click <strong>“Clear”</strong> to reset the tool and enter a new token.</li>
        </ul>
        <p className="text-gray-600">
          This JWT decoder decodes the header, payload, and signature of your JWT token. It's useful for debugging, checking token validity, and inspecting claims.
        </p>
        <p className="mt-4 text-gray-600">
          The tool is 100% client-side, meaning no data is sent to our servers. Your data stays private and secure.
        </p>
      </section>
    </>
  );
}
